type NullableDateValue = string | null | undefined;

export interface BadgeConfiguration {
  badgeName?: string;
  badgeKey?: string;
  badgeColor?: string;
  badgeTextColor?: string;
  Enabled?: boolean;
  /** When false, no lower-priority badges are shown after this one (inclusive cap). When true/omitted, more badges may follow. */
  stackBadge?: boolean | string | number;
  /** Optional full-bleed badge artwork (URL or site-relative path). */
  backgroundImage?: string;
  /** Schedule on the badge config (camelCase or snake_case from API). */
  startDate?: NullableDateValue;
  endDate?: NullableDateValue;
  start_date?: NullableDateValue;
  end_date?: NullableDateValue;
  /** e.g. `topRightCorder`, `topLeftCorder` (CMS spelling). */
  position?: string;
  /** e.g. `circle`, `square`. */
  shape?: string;
  /** Some API payloads use PascalCase; normalized onto `shape` / `position` when building the badge map. */
  Shape?: string;
  Position?: string;
  shape_type?: string;
}

export interface ProductCategoryAncestor {
  slug?: string;
}

export interface ProductCategoryForBadging {
  slug?: string;
  ancestors?: ProductCategoryAncestor[];
}

export interface ProductAttributeRaw {
  name?: string;
  value?: unknown;
}

export interface ProductBadgingRuleGroupItem {
  type?: string;
  value?: string;
}

export interface ProductBadgingRuleCondition {
  condition_type?: string;
  badge_key?: string;
  group?: ProductBadgingRuleGroupItem[];
}

export interface ProductBadgingRule {
  start_date?: NullableDateValue;
  end_date?: NullableDateValue;
  priority?: number;
  rule_enabled?: boolean;
  conditions?: ProductBadgingRuleCondition[];
}

export interface ProductBadgingEntry {
  rules?: ProductBadgingRule[];
}

export interface ProductBadgingUtilInput {
  productBadgingEntries?: ProductBadgingEntry[] | null;
  badgeConfigurations?: BadgeConfiguration[] | null;
  productCategories?: ProductCategoryForBadging[] | null;
  initialSizeKey?: string | null;
  initialColorKey?: string | null;
  /** Size / selected variant row from Commercetools (primary for `overrideBadgeLabels`, etc.). */
  attributesRaw?: ProductAttributeRaw[] | null;
  /** e.g. color `masterVariant.attributesRaw` — merged after primary so primary wins on duplicate `name`. */
  attributesRawSecondary?: ProductAttributeRaw[] | null;
  now?: Date;
  /**
   * When a finite number > 0, cap how many badges are returned after stack + override (e.g. `maxBadgesPdp`).
   * `0` or `undefined` means no cap.
   */
  maxVisibleBadges?: number;
}

const toComparableString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim().toLowerCase();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).toLowerCase();
  return String(value).trim().toLowerCase();
};

/**
 * Concatenates variant attribute rows for rule matching / overrides.
 * When the same `name` exists on both sides (e.g. size + color), **primary wins**: the secondary row is omitted
 * so `parseOverrideBadgeLabelKeys` and similar `.find()` logic see one row per name (the size / hit row).
 */
export const mergeAttributesRawForBadging = (
  primary?: ProductAttributeRaw[] | null,
  secondary?: ProductAttributeRaw[] | null,
): ProductAttributeRaw[] => {
  const p = Array.isArray(primary) ? primary : [];
  const s = Array.isArray(secondary) ? secondary : [];
  const primaryNames = new Set(p.map((a) => toComparableString(a?.name)).filter((name) => name.length > 0));
  return [...p, ...s.filter((a) => !primaryNames.has(toComparableString(a?.name)))];
};

const normalizeCategorySlug = (slug: unknown): string => {
  const normalizedSlug = toComparableString(slug);
  if (!normalizedSlug) return '';
  return normalizedSlug.split('_')[0] || normalizedSlug;
};

const isEnabledFlag = (value: unknown): boolean => {
  const normalized = toComparableString(value);
  return normalized === 'true' || normalized === '1';
};

/** Merge camelCase / PascalCase / snake-ish keys so PLP/PDP both read the same display fields from CT/Commercetools. */
const canonicalizeBadgeConfigurationRow = (configuration: BadgeConfiguration): BadgeConfiguration => {
  const c = configuration as BadgeConfiguration;
  const shapeResolved = [c.shape, c.Shape, c.shape_type].find((v) => typeof v === 'string' && String(v).trim());
  const positionResolved = [c.position, c.Position].find((v) => typeof v === 'string' && String(v).trim());
  const shape = shapeResolved != null ? String(shapeResolved).trim() : undefined;
  const position = positionResolved != null ? String(positionResolved).trim() : undefined;
  return {
    ...configuration,
    ...(shape ? { shape } : {}),
    ...(position ? { position } : {}),
  };
};

const parseDateValue = (value?: NullableDateValue): Date | null => {
  if (!value) return null;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

/** Rules are evaluated only by `rule_enabled`; scheduling uses `BadgeConfiguration.startDate` / `endDate`. */
const isRuleEnabledForMatching = (rule: ProductBadgingRule): boolean => rule?.rule_enabled !== false;

const getBadgeConfigScheduleBounds = (configuration: BadgeConfiguration): { start: Date | null; end: Date | null } => {
  const start = parseDateValue(configuration?.startDate ?? configuration?.start_date);
  const end = parseDateValue(configuration?.endDate ?? configuration?.end_date);
  return { start, end };
};

/** Badge is within its own start/end window (if any). */
export const isBadgeConfigurationScheduleActive = (configuration: BadgeConfiguration, now: Date): boolean => {
  const { start, end } = getBadgeConfigScheduleBounds(configuration);
  if (start && now < start) return false;
  if (end && now > end) return false;
  return true;
};

const buildCategorySlugSet = (categories?: ProductCategoryForBadging[] | null): Set<string> => {
  const categorySlugs = new Set<string>();
  if (!Array.isArray(categories)) return categorySlugs;

  categories.forEach((category) => {
    if (category?.slug) {
      const normalizedSlug = normalizeCategorySlug(category.slug);
      if (normalizedSlug) {
        categorySlugs.add(normalizedSlug);
      }
    }
    category?.ancestors?.forEach((ancestor) => {
      if (ancestor?.slug) {
        const normalizedSlug = normalizeCategorySlug(ancestor.slug);
        if (normalizedSlug) {
          categorySlugs.add(normalizedSlug);
        }
      }
    });
  });

  return categorySlugs;
};

const buildAttributeMap = (attributesRaw?: ProductAttributeRaw[] | null): Map<string, unknown> => {
  const attributeMap = new Map<string, unknown>();
  if (!Array.isArray(attributesRaw)) return attributeMap;

  attributesRaw.forEach((attribute) => {
    if (!attribute?.name) return;
    attributeMap.set(toComparableString(attribute.name), attribute.value);
  });

  return attributeMap;
};

type ParsedAttributeCondition =
  | { kind: 'equals'; attributeName: string; expectedValue: string }
  | { kind: 'order'; op: 'gt' | 'lt' | 'gte' | 'lte'; attributeName: string; operand: string };

const splitCommaSeparatedRuleParts = (raw: string | undefined | null): string[] => {
  if (raw == null || String(raw).trim() === '') return [];
  return String(raw)
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
};

/**
 * Parses CMS `value` strings such as `averageRating=4.5`, `averageRating>4.5`, `averageRating>=4.5`,
 * `averageRating>4.5,5.8,3.4` (comma-separated thresholds for `>` / `<` are OR),
 * `primaryImageDate=<04/23/2026`. Uses the leftmost valid operator token.
 */
const parseAttributeCondition = (value?: string): ParsedAttributeCondition | null => {
  if (!value || !String(value).trim()) return null;
  const s = String(value).trim();
  const tokenMatch = s.match(/(>=|<=|=>|=<|=|>|<)/);
  if (!tokenMatch || tokenMatch.index == null) return null;
  const rawOp = tokenMatch[1];
  const opIndex = tokenMatch.index;
  const attributeName = s.slice(0, opIndex).trim();
  const rhs = s.slice(opIndex + rawOp.length).trim();
  if (!attributeName || !rhs) return null;
  if (rawOp === '=') return { kind: 'equals', attributeName, expectedValue: rhs };
  const normalizedOp = rawOp === '=>' ? '>=' : rawOp === '=<' ? '<=' : rawOp;
  if (normalizedOp === '>') return { kind: 'order', op: 'gt', attributeName, operand: rhs };
  if (normalizedOp === '<') return { kind: 'order', op: 'lt', attributeName, operand: rhs };
  if (normalizedOp === '>=') return { kind: 'order', op: 'gte', attributeName, operand: rhs };
  return { kind: 'order', op: 'lte', attributeName, operand: rhs };
};

const parseUsSlashDateMs = (s: string): number | null => {
  const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d.getTime();
};

const parseToTimeMs = (val: unknown): number | null => {
  if (val instanceof Date && !Number.isNaN(val.getTime())) return val.getTime();
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (typeof val !== 'string') return null;
  const s = val.trim();
  if (!s) return null;
  const us = parseUsSlashDateMs(s);
  if (us != null) return us;
  const isoDateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s]|$)/);
  if (isoDateOnly) {
    const year = Number(isoDateOnly[1]);
    const month = Number(isoDateOnly[2]);
    const day = Number(isoDateOnly[3]);
    const d = new Date(year, month - 1, day);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  const parsed = Date.parse(s);
  return Number.isNaN(parsed) ? null : parsed;
};

const compareScalarForOrder = (actual: unknown, op: 'gt' | 'lt' | 'gte' | 'lte', operandStr: string): boolean => {
  const rhs = operandStr.trim();
  if (!rhs) return false;

  const leftStr = typeof actual === 'string' ? actual.trim() : String(actual ?? '').trim();
  const aNum = typeof actual === 'number' && Number.isFinite(actual) ? actual : Number(leftStr);
  const bNum = Number(rhs);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
    if (op === 'gt') return aNum > bNum;
    if (op === 'lt') return aNum < bNum;
    if (op === 'gte') return aNum >= bNum;
    return aNum <= bNum;
  }

  const aTime = parseToTimeMs(actual);
  const bTime = parseToTimeMs(rhs);
  if (aTime != null && bTime != null) {
    if (op === 'gt') return aTime > bTime;
    if (op === 'lt') return aTime < bTime;
    if (op === 'gte') return aTime >= bTime;
    return aTime <= bTime;
  }

  const aLex = toComparableString(actual);
  const bLex = toComparableString(rhs);
  if (!aLex || !bLex) return false;
  if (op === 'gt') return aLex > bLex;
  if (op === 'lt') return aLex < bLex;
  if (op === 'gte') return aLex >= bLex;
  return aLex <= bLex;
};

const matchesOrderedAttribute = (actual: unknown, op: 'gt' | 'lt' | 'gte' | 'lte', operandStr: string): boolean => {
  if (actual === undefined || actual === null) return false;
  if (Array.isArray(actual)) {
    return actual.some((item) => item != null && compareScalarForOrder(item, op, operandStr));
  }
  return compareScalarForOrder(actual, op, operandStr);
};

const matchesStaticSku = (
  expectedSku?: string,
  initialSizeKey?: string | null,
  initialColorKey?: string | null,
): boolean => {
  if (!expectedSku) return false;
  const candidates = splitCommaSeparatedRuleParts(expectedSku).map((s) => toComparableString(s));
  if (!candidates.length) return false;
  const sizeKey = toComparableString(initialSizeKey);
  const colorKey = toComparableString(initialColorKey);
  return candidates.some((c) => c === sizeKey || c === colorKey);
};

const matchesCategory = (expectedSlug?: string, categorySlugSet?: Set<string>): boolean => {
  if (!expectedSlug || !categorySlugSet) return false;
  const slugs = splitCommaSeparatedRuleParts(expectedSlug)
    .map((part) => normalizeCategorySlug(part))
    .filter((slug) => slug.length > 0);
  if (!slugs.length) return false;
  return slugs.some((slug) => categorySlugSet.has(slug));
};

const matchesAttribute = (attributeCondition?: string, attributeMap?: Map<string, unknown>): boolean => {
  if (!attributeCondition || !attributeMap) return false;
  const parsedCondition = parseAttributeCondition(attributeCondition);
  if (!parsedCondition) return false;

  const actualValue = attributeMap.get(toComparableString(parsedCondition.attributeName));
  if (actualValue === undefined || actualValue === null) return false;

  if (parsedCondition.kind === 'order') {
    const operands = splitCommaSeparatedRuleParts(parsedCondition.operand);
    if (!operands.length) return false;
    return operands.some((part) => matchesOrderedAttribute(actualValue, parsedCondition.op, part));
  }

  const expectedTokens = splitCommaSeparatedRuleParts(parsedCondition.expectedValue).map((s) => toComparableString(s));
  if (!expectedTokens.length) return false;
  if (Array.isArray(actualValue)) {
    const actualTokens = actualValue
      .filter((item) => item != null)
      .map((item) => toComparableString(item))
      .filter((t) => t.length > 0);
    return actualTokens.some((a) => expectedTokens.includes(a));
  }

  return expectedTokens.includes(toComparableString(actualValue));
};

const doesGroupItemMatch = (
  groupItem: ProductBadgingRuleGroupItem,
  context: {
    categorySlugSet: Set<string>;
    attributeMap: Map<string, unknown>;
    initialSizeKey?: string | null;
    initialColorKey?: string | null;
  },
): boolean => {
  const itemType = toComparableString(groupItem?.type);
  if (itemType === 'static sku') {
    return matchesStaticSku(groupItem?.value, context.initialSizeKey, context.initialColorKey);
  }
  if (itemType === 'category') {
    return matchesCategory(groupItem?.value, context.categorySlugSet);
  }
  if (itemType === 'product attribute' || itemType === 'variant attribute') {
    return matchesAttribute(groupItem?.value, context.attributeMap);
  }
  return false;
};

const doesConditionMatch = (
  condition: ProductBadgingRuleCondition,
  context: {
    categorySlugSet: Set<string>;
    attributeMap: Map<string, unknown>;
    initialSizeKey?: string | null;
    initialColorKey?: string | null;
  },
): boolean => {
  const groupItems = Array.isArray(condition?.group) ? condition.group : [];
  if (!groupItems.length) return false;

  const conditionType = toComparableString(condition?.condition_type);
  if (conditionType === 'and') {
    return groupItems.every((groupItem) => doesGroupItemMatch(groupItem, context));
  }

  return groupItems.some((groupItem) => doesGroupItemMatch(groupItem, context));
};

const isStackingWithLowerPriority = (configuration: BadgeConfiguration): boolean => {
  const value = configuration?.stackBadge;
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized !== 'false' && normalized !== '0';
  }
  return true;
};

/**
 * Badges are already in rule priority order. Walk that order: while `stackBadge` is stacking (true/omitted),
 * keep including badges. The first badge with `stackBadge` explicitly off ends the visible list (inclusive);
 * anything matched after it is hidden so only the capped stack shows.
 */
export const applyStackBadgeDisplayRules = (orderedBadges: BadgeConfiguration[]): BadgeConfiguration[] => {
  if (!Array.isArray(orderedBadges) || !orderedBadges.length) return [];
  const cutIndex = orderedBadges.findIndex((badge) => !isStackingWithLowerPriority(badge));
  if (cutIndex === -1) return orderedBadges;
  return orderedBadges.slice(0, cutIndex + 1);
};

const OVERRIDE_BADGE_LABELS_ATTR = 'overridebadgelabels';

/** Ordered, normalized `badgeKey` values from `overrideBadgeLabels` (deduped, first occurrence wins). */
export const parseOverrideBadgeLabelKeys = (attributesRaw?: ProductAttributeRaw[] | null): string[] | null => {
  if (!Array.isArray(attributesRaw) || !attributesRaw.length) return null;
  const row = attributesRaw.find((a) => toComparableString(a?.name) === OVERRIDE_BADGE_LABELS_ATTR);
  if (!row || row.value === undefined || row.value === null) return null;

  const rawKeys: string[] = [];
  if (Array.isArray(row.value)) {
    row.value.forEach((v) => {
      if (v != null && String(v).trim()) rawKeys.push(toComparableString(v));
    });
  } else if (typeof row.value === 'string') {
    row.value.split(',').forEach((part) => {
      const t = part.trim();
      if (t) rawKeys.push(toComparableString(t));
    });
  } else {
    rawKeys.push(toComparableString(row.value));
  }

  const seen = new Set<string>();
  const orderedUnique: string[] = [];
  rawKeys.forEach((key) => {
    if (!key || seen.has(key)) return;
    seen.add(key);
    orderedUnique.push(key);
  });

  return orderedUnique.length ? orderedUnique : null;
};

/**
 * When `overrideBadgeLabels` is set on the product, **add** those badge keys (from `badgeConfigurations`)
 * on top of rule-matched badges. Final display order follows `badgeConfigurations` index (not rule/append order).
 */
const resolveBadgesWithOverrideAllowlist = (
  afterStack: BadgeConfiguration[],
  mergedAttributesRaw: ProductAttributeRaw[],
  enabledBadgeConfigMap: Map<string, BadgeConfiguration>,
): BadgeConfiguration[] => {
  const allowKeys = parseOverrideBadgeLabelKeys(mergedAttributesRaw.length ? mergedAttributesRaw : null);
  if (!allowKeys?.length) {
    return afterStack;
  }

  const seen = new Set<string>();
  const out: BadgeConfiguration[] = [];

  for (const b of afterStack) {
    const k = toComparableString(b.badgeKey);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(b);
  }

  for (const key of allowKeys) {
    if (!key || seen.has(key)) continue;
    const configuration = enabledBadgeConfigMap.get(key);
    if (!configuration) continue;
    seen.add(key);
    out.push({ ...configuration });
  }

  return out;
};

/**
 * Same union semantics as inside `getOrderedMatchedBadges`: keep all `orderedBadges`, then append any
 * `overrideBadgeLabels` keys that resolve to badges already in the list but were missing from the array
 * (unusual). Keys not present in `orderedBadges` cannot be added here (no config map).
 */
export const applyOverrideBadgeLabelsFilter = (
  orderedBadges: BadgeConfiguration[],
  attributesRaw?: ProductAttributeRaw[] | null,
): BadgeConfiguration[] => {
  if (!Array.isArray(orderedBadges) || !orderedBadges.length) return orderedBadges;
  const allowKeys = parseOverrideBadgeLabelKeys(attributesRaw);
  if (!allowKeys?.length) return orderedBadges;

  const badgeByKey = new Map<string, BadgeConfiguration>();
  orderedBadges.forEach((b) => {
    const k = toComparableString(b.badgeKey);
    if (k && !badgeByKey.has(k)) badgeByKey.set(k, b);
  });

  const seen = new Set<string>();
  const out: BadgeConfiguration[] = [];
  for (const b of orderedBadges) {
    const k = toComparableString(b.badgeKey);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(b);
  }
  for (const key of allowKeys) {
    if (!key || seen.has(key)) continue;
    const configuration = badgeByKey.get(key);
    if (!configuration) continue;
    seen.add(key);
    out.push({ ...configuration });
  }
  return out;
};

const buildBadgeConfigIndexByKey = (badgeConfigurations: BadgeConfiguration[]): Map<string, number> => {
  const indexByKey = new Map<string, number>();
  badgeConfigurations.forEach((configuration, index) => {
    const key = toComparableString(configuration?.badgeKey);
    if (key && !indexByKey.has(key)) indexByKey.set(key, index);
  });
  return indexByKey;
};

/** Lower `badgeConfigurations` index = higher display priority. */
const sortBadgesByConfigurationOrder = (
  badges: BadgeConfiguration[],
  badgeConfigurations: BadgeConfiguration[],
): BadgeConfiguration[] => {
  if (!Array.isArray(badges) || badges.length <= 1) return badges;
  const indexByKey = buildBadgeConfigIndexByKey(badgeConfigurations);
  return [...badges].sort((a, b) => {
    const ia = indexByKey.get(toComparableString(a.badgeKey)) ?? Number.MAX_SAFE_INTEGER;
    const ib = indexByKey.get(toComparableString(b.badgeKey)) ?? Number.MAX_SAFE_INTEGER;
    return ia - ib;
  });
};

/**
 * Contentstack rules only decide **which** `badge_key` values match (SKU / category / attributes).
 * All display fields (`badgeName`, `backgroundImage`, `position`, `shape`, colors, dates on the badge, etc.)
 * always come from **`badgeConfigurations`** (`projectSettings.badgeConfigurations`), keyed by `badgeKey`.
 */
export const getOrderedMatchedBadges = ({
  productBadgingEntries,
  badgeConfigurations,
  productCategories,
  initialSizeKey,
  initialColorKey,
  attributesRaw,
  attributesRawSecondary,
  now = new Date(),
  maxVisibleBadges,
}: ProductBadgingUtilInput): BadgeConfiguration[] => {
  if (!Array.isArray(productBadgingEntries) || !Array.isArray(badgeConfigurations)) return [];

  const mergedAttributesRaw = mergeAttributesRawForBadging(attributesRaw, attributesRawSecondary);

  const enabledBadgeConfigMap = new Map<string, BadgeConfiguration>();
  badgeConfigurations.forEach((configuration) => {
    if (!configuration?.badgeKey || !isEnabledFlag(configuration?.Enabled)) return;
    if (!isBadgeConfigurationScheduleActive(configuration, now)) return;
    enabledBadgeConfigMap.set(
      toComparableString(configuration.badgeKey),
      canonicalizeBadgeConfigurationRow({ ...configuration }),
    );
  });
  if (!enabledBadgeConfigMap.size) return [];

  const rules = productBadgingEntries.flatMap((entry) => (Array.isArray(entry?.rules) ? entry.rules : []));

  const activeRulesInOrder = rules.filter((rule) => isRuleEnabledForMatching(rule));

  const categorySlugSet = buildCategorySlugSet(productCategories);
  const attributeMap = buildAttributeMap(mergedAttributesRaw.length ? mergedAttributesRaw : undefined);
  const matchedBadgeKeys = new Set<string>();
  const orderedBadges: BadgeConfiguration[] = [];

  activeRulesInOrder.forEach((rule) => {
    const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
    conditions.forEach((condition) => {
      const conditionBadgeKey = toComparableString(condition?.badge_key);
      if (!conditionBadgeKey || matchedBadgeKeys.has(conditionBadgeKey)) return;
      if (
        !doesConditionMatch(condition, {
          categorySlugSet,
          attributeMap,
          initialSizeKey,
          initialColorKey,
        })
      ) {
        return;
      }

      const configuration = enabledBadgeConfigMap.get(conditionBadgeKey);
      if (!configuration) return;

      matchedBadgeKeys.add(conditionBadgeKey);
      orderedBadges.push({ ...configuration });
    });
  });

  const afterStack = applyStackBadgeDisplayRules(sortBadgesByConfigurationOrder(orderedBadges, badgeConfigurations));
  let result = resolveBadgesWithOverrideAllowlist(afterStack, mergedAttributesRaw, enabledBadgeConfigMap);
  // Override keys are appended after rule matches; re-sort so studio priority applies before the cap.
  result = sortBadgesByConfigurationOrder(result, badgeConfigurations);
  if (typeof maxVisibleBadges === 'number' && maxVisibleBadges > 0) {
    result = result.slice(0, maxVisibleBadges);
  }
  return result;
};
